'use client'
import { useState, type RefObject } from 'react'
import {
  useScroll,
  useTransform,
  useMotionTemplate,
  useMotionValueEvent,
  type MotionValue,
} from 'framer-motion'
import { type VisualKind } from './visuals'

export interface WhyTimeline {
  /** Track transform for the horizontal filmstrip (`translateX(…vw)`). */
  trackT: MotionValue<string>
  /** Per-chapter draw progress (0→1) over that chapter's hold window. */
  drawFor: (k: VisualKind) => MotionValue<number>
  /** Index of the chapter currently centred — drives the SR readout. */
  active: number
  /** Raw 0→1 scroll progress through the whole filmstrip (drives the bar). It
   *  reaches 1 exactly as the last chapter finishes its hold (no trailing pan). */
  progress: MotionValue<number>
}

/**
 * Stepped "scrollytelling" timeline for the desktop filmstrip. Each chapter
 * HOLDS (track pinned, centred) while its diagram draws, then the track PANS to
 * the next chapter — stop → read → scroll fills the lines → advance.
 *
 * Builds alternating hold (flat) / pan (ramp) keyframes; `dir` flips for RTL
 * since the flex track already lays chapter 1 at the right. Extracted from the
 * section component so the layout/JSX stays readable. Assumes 5 chapters
 * (matches VISUALS) — the per-chapter draw values are fixed at that count.
 */
export function useWhyTimeline(
  ref: RefObject<HTMLDivElement | null>,
  N: number,
  isRTL: boolean,
): WhyTimeline {
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end end'],
  })

  const HOLD = 1.5 // relative scroll weight of each chapter's draw window
  const PAN = 1 // relative weight of each chapter→chapter transition
  const dir = isRTL ? 1 : -1
  const stops: number[] = [0]
  const xKf: number[] = [0]
  const holds: [number, number][] = []
  let acc = 0
  for (let i = 0; i < N; i++) {
    const hStart = acc
    acc += HOLD
    stops.push(acc)
    xKf.push(dir * i * 100)
    holds.push([hStart, acc])
    if (i < N - 1) {
      acc += PAN
      stops.push(acc)
      xKf.push(dir * (i + 1) * 100)
    }
  }
  const inputs = stops.map((s) => s / acc)
  const holdRanges = holds.map(([a, b]) => [a / acc, b / acc] as [number, number])

  const trackX = useTransform(scrollYProgress, inputs, xKf)
  const trackT = useMotionTemplate`translateX(${trackX}vw)`

  // Each animated diagram fills over the FIRST part of its chapter's hold, then
  // the chapter DWELLS (pinned, fully drawn) for the rest of the hold before the
  // pan — so it settles after landing instead of snapping straight to the next.
  const DRAW_FRAC = 0.85
  const dwin = (i: number): [number, number] => {
    const [a, b] = holdRanges[i]
    return [a, a + (b - a) * DRAW_FRAC]
  }
  const setupDraw = useTransform(scrollYProgress, dwin(0), [0, 1])
  const sourceDraw = useTransform(scrollYProgress, dwin(1), [0, 1])
  const stockDraw = useTransform(scrollYProgress, dwin(2), [0, 1])
  const routeDraw = useTransform(scrollYProgress, dwin(3), [0, 1])
  const chainDraw = useTransform(scrollYProgress, dwin(N - 1), [0, 1])
  const drawFor = (k: VisualKind): MotionValue<number> =>
    k === 'source'
      ? sourceDraw
      : k === 'stock'
        ? stockDraw
        : k === 'route'
          ? routeDraw
          : k === 'chain'
            ? chainDraw
            : setupDraw

  const [active, setActive] = useState(0)
  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    let a = 0
    for (let i = 0; i < N; i++) if (v >= holdRanges[i][0]) a = i
    setActive(a)
  })

  return { trackT, drawFor, active, progress: scrollYProgress }
}
